import React, { useEffect, useMemo, useState, createElement } from 'react';
import { XIcon, SendIcon, ChevronDownIcon, CheckSquareIcon, SquareIcon, LayoutGridIcon, ImageIcon, MonitorIcon, PlusIcon, UserIcon, ReceiptIcon, DownloadIcon } from 'lucide-react';
import { Brand, Store, MediaType, StoreTier, BrandContact, SlotStatus } from '../../data/types';
import { brands } from '../../data/brands';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { generateEstimatePDF } from './EstimatePDF';
interface BrandRequestModalProps {
  isOpen: boolean;
  stores: Store[];
  editingDraft?: import('../../data/types').BrandRequest | null;
  preSelectedStoreIds?: string[];
  onClose: () => void;
  onSendRequest: (request: any, sendNow?: boolean) => void;
  onUpdateRequest?: (requestId: string, request: any, sendNow?: boolean) => void;
  isBrandInitiated?: boolean;
  brandName?: string;
}
const mediaTypeIcons: Record<MediaType, React.ReactNode> = {
  'Digital Outposts': <LayoutGridIcon className="w-4 h-4" data-id="element-85" />,
  'Digital Drive Aisle': <ImageIcon className="w-4 h-4" data-id="element-86" />,
  'Digital Windows': <LayoutGridIcon className="w-4 h-4" data-id="element-87" />
};
const allMediaTypes: MediaType[] = ['Digital Outposts', 'Digital Drive Aisle'];
const priceByMediaType: Record<MediaType, number> = {
  'Digital Outposts': 3250,
  'Digital Drive Aisle': 3750,
  'Digital Windows': 3500
};
function formatZAR(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 0
  }).format(amount);
}
export function BrandRequestModal({
  isOpen,
  stores,
  editingDraft,
  preSelectedStoreIds = [],
  onClose,
  onSendRequest,
  onUpdateRequest,
  isBrandInitiated = false,
  brandName = ''
}: BrandRequestModalProps) {
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  // Set initial brand if brand initiated
  useEffect(() => {
    if (isOpen && isBrandInitiated && brandName) {
      const brand = brands.find(b => b.name === brandName);
      if (brand) setSelectedBrand(brand.id);
    }
  }, [isOpen, isBrandInitiated, brandName]);
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(new Set());
  const [selectedPositionIds, setSelectedPositionIds] = useState<Set<string>>(new Set());
  const [expandedStoreId, setExpandedStoreId] = useState<string | null>(null);
  const [positionTypeFilter, setPositionTypeFilter] = useState<'all' | 'outpost' | 'cylinder'>('all');
  const [startWeek, setStartWeek] = useState<number>(1);
  const [endWeek, setEndWeek] = useState<number>(1);
  const [campaignName, setCampaignName] = useState('');
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendToBrand, setSendToBrand] = useState(false);
  const [showEstimate, setShowEstimate] = useState(false);
  // Cost estimate calculation
  const costEstimate = useMemo(() => {
    if (selectedStoreIds.size === 0 || selectedPositionIds.size === 0) {
      return {
        lineItems: [],
        total: 0
      };
    }
    const weekCount = Math.max(1, endWeek - startWeek + 1);
    const selectedStoresList = stores.filter(s => selectedStoreIds.has(s.id));
    let outpostCount = 0;
    let cylinderCount = 0;
    const uniqueStoresWithOutposts = new Set<string>();
    const uniqueStoresWithCylinders = new Set<string>();
    selectedStoresList.forEach(store => {
      store.positions?.forEach(p => {
        if (selectedPositionIds.has(p.id)) {
          if (p.type === 'outpost') {
            outpostCount++;
            uniqueStoresWithOutposts.add(store.id);
          }
          if (p.type === 'cylinder') {
            cylinderCount++;
            uniqueStoresWithCylinders.add(store.id);
          }
        }
      });
    });
    const lineItems: {
      mediaType: MediaType;
      storeCount: number;
      unitPrice: number;
      subtotal: number;
      positionCount: number;
    }[] = [];
    if (outpostCount > 0) {
      lineItems.push({
        mediaType: 'Digital Outposts',
        storeCount: uniqueStoresWithOutposts.size,
        unitPrice: priceByMediaType['Digital Outposts'],
        subtotal: outpostCount * priceByMediaType['Digital Outposts'] * weekCount,
        positionCount: outpostCount
      });
    }
    if (cylinderCount > 0) {
      lineItems.push({
        mediaType: 'Digital Drive Aisle',
        storeCount: uniqueStoresWithCylinders.size,
        unitPrice: priceByMediaType['Digital Drive Aisle'],
        subtotal: cylinderCount * priceByMediaType['Digital Drive Aisle'] * weekCount,
        positionCount: cylinderCount
      });
    }
    const total = lineItems.reduce((sum, item) => sum + item.subtotal, 0);
    return {
      lineItems,
      total
    };
  }, [selectedStoreIds, selectedPositionIds, stores, startWeek, endWeek]);
  // Contacts state
  const [contacts, setContacts] = useState<BrandContact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    jobTitle: ''
  });
  // Pre-select stores from dashboard selection
  useEffect(() => {
    if (isOpen && !editingDraft && preSelectedStoreIds.length > 0) {
      setSelectedStoreIds(new Set(preSelectedStoreIds));
    }
  }, [isOpen, editingDraft, preSelectedStoreIds]);
  // Populate form when editing a draft
  useEffect(() => {
    if (editingDraft && isOpen) {
      setSelectedBrand(editingDraft.brandId);
      setSelectedStoreIds(new Set(editingDraft.stores.map(s => s.storeId)));
      setSelectedPositionIds(new Set(editingDraft.positionIds || []));
      setStartWeek(editingDraft.startWeek || 1);
      setEndWeek(editingDraft.endWeek || 1);
      setCampaignName(editingDraft.campaignName || '');
      setSendToBrand(false);
    }
  }, [editingDraft, isOpen]);
  // Load contacts when brand changes
  useEffect(() => {
    if (selectedBrand) {
      const brand = brands.find(b => b.id === selectedBrand);
      const defaultContacts = brand?.contacts || [];
      // Load from local storage
      const storedContactsStr = localStorage.getItem(`brand-contacts-${selectedBrand}`);
      const storedContacts: BrandContact[] = storedContactsStr ? JSON.parse(storedContactsStr) : [];
      // Merge, preferring stored over default if IDs match
      const merged = [...defaultContacts];
      storedContacts.forEach(sc => {
        if (!merged.find(mc => mc.id === sc.id)) {
          merged.push(sc);
        }
      });
      setContacts(merged);
      if (merged.length > 0) {
        setSelectedContactId(merged[0].id);
      } else {
        setSelectedContactId('');
      }
      setIsAddingContact(merged.length === 0);
    } else {
      setContacts([]);
      setSelectedContactId('');
      setIsAddingContact(false);
    }
  }, [selectedBrand]);
  if (!isOpen) return null;
  const displayedBrands = showAllBrands ? brands : brands.slice(0, 6);
  const handleSaveNewContact = () => {
    if (!newContact.name || !newContact.email) return;
    const contact: BrandContact = {
      id: `contact-${Date.now()}`,
      ...newContact
    };
    const updatedContacts = [...contacts, contact];
    setContacts(updatedContacts);
    setSelectedContactId(contact.id);
    setIsAddingContact(false);
    setNewContact({
      name: '',
      email: '',
      jobTitle: ''
    });
    // Save to local storage
    localStorage.setItem(`brand-contacts-${selectedBrand}`, JSON.stringify(updatedContacts));
  };
  const toggleStore = (storeId: string) => {
    setSelectedStoreIds(prev => {
      const next = new Set(prev);
      if (next.has(storeId)) {
        next.delete(storeId);
        // Remove this store's positions
        const store = stores.find(s => s.id === storeId);
        if (store?.positions) {
          setSelectedPositionIds(prevPos => {
            const nextPos = new Set(prevPos);
            store.positions.forEach(p => nextPos.delete(p.id));
            return nextPos;
          });
        }
        if (expandedStoreId === storeId) setExpandedStoreId(null);
      } else {
        next.add(storeId);
        // Auto-expand the newly selected store so user can pick positions
        setExpandedStoreId(storeId);
      }
      return next;
    });
  };
  const toggleAllStores = () => {
    if (selectedStoreIds.size === stores.length) {
      setSelectedStoreIds(new Set());
      setSelectedPositionIds(new Set());
      setExpandedStoreId(null);
    } else {
      setSelectedStoreIds(new Set(stores.map(s => s.id)));
      // Don't auto-select positions — user must choose explicitly
    }
  };
  const handleSubmit = async () => {
    if (!selectedBrand || selectedStoreIds.size === 0 || selectedPositionIds.size === 0 || startWeek > endWeek) return;
    // If sending to brand, contact is required
    if (sendToBrand && !hasContact) return;
    setIsSubmitting(true);
    const brand = brands.find(b => b.id === selectedBrand);
    const selectedStoresData = stores.filter(s => selectedStoreIds.has(s.id)).map(s => ({
      storeId: s.id,
      storeName: s.name
    }));
    let contactInfo = {};
    if (selectedContactId) {
      const contact = contacts.find(c => c.id === selectedContactId);
      if (contact) {
        contactInfo = {
          contactName: contact.name,
          contactEmail: contact.email,
          contactJobTitle: contact.jobTitle
        };
      }
    } else if (isAddingContact && newContact.name && newContact.email) {
      contactInfo = {
        contactName: newContact.name,
        contactEmail: newContact.email,
        contactJobTitle: newContact.jobTitle
      };
      handleSaveNewContact();
    }
    if (brand && selectedStoresData.length > 0) {
      await new Promise(resolve => setTimeout(resolve, 600));
      const derivedMediaTypes = new Set<MediaType>();
      selectedStoresData.forEach(s => {
        const store = stores.find(st => st.id === s.storeId);
        store?.positions?.forEach(p => {
          if (selectedPositionIds.has(p.id)) {
            derivedMediaTypes.add(p.type === 'outpost' ? 'Digital Outposts' : 'Digital Drive Aisle');
          }
        });
      });
      const requestData = {
        brandId: brand.id,
        brandName: brand.name,
        stores: selectedStoresData,
        mediaType: Array.from(derivedMediaTypes)[0] || 'Digital Outposts',
        mediaTypes: Array.from(derivedMediaTypes),
        positionIds: Array.from(selectedPositionIds),
        weekNumber: startWeek,
        endWeekNumber: startWeek !== endWeek ? endWeek : undefined,
        weekStartDate: '2026-03-03',
        weekEndDate: '2026-03-09',
        campaignName: campaignName || undefined,
        ...(isBrandInitiated ? {
          requestedBy: 'brand' as const
        } : {}),
        ...contactInfo
      };
      if (editingDraft && onUpdateRequest) {
        onUpdateRequest(editingDraft.id, requestData, sendToBrand);
      } else {
        // Brand-initiated requests always go to pending
        onSendRequest(requestData, isBrandInitiated ? true : sendToBrand);
      }
      setIsSubmitting(false);
      onClose();
      // Reset form
      setSelectedBrand('');
      setSelectedStoreIds(new Set());
      setSelectedPositionIds(new Set());
      setStartWeek(1);
      setEndWeek(1);
      setCampaignName('');
      setSendToBrand(false);
      setExpandedStoreId(null);
    }
  };
  const isBaseValid = selectedBrand && selectedStoreIds.size > 0 && selectedPositionIds.size > 0 && startWeek <= endWeek;
  const hasContact = selectedContactId || isAddingContact && newContact.name && newContact.email;
  const isFormValid = isBrandInitiated ? isBaseValid : isBaseValid && (!sendToBrand || hasContact);
  return <>
      <div className="fixed inset-0 bg-black/40 z-50 transition-opacity" onClick={onClose} data-id="element-88" />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-89">
        <div className="bg-white w-full max-w-2xl shadow-2xl pointer-events-auto flex flex-col max-h-[90vh] rounded-xl" data-id="element-90">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border" data-id="element-91">
            <h2 className="text-lg font-semibold text-text-primary" data-id="element-92">
              {editingDraft ? 'Edit Draft' : isBrandInitiated ? 'Request Media Space' : 'Invite Brand to Book'}
            </h2>
            <button onClick={onClose} className="p-1.5 text-text-tertiary hover:text-text-primary transition-colors rounded-sm hover:bg-surface" data-id="element-93">
              <XIcon className="w-5 h-5" data-id="element-94" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1 space-y-8" data-id="element-95">
            {/* 1. Brand Selection */}
            {isBrandInitiated ? <div className="bg-surface-warm p-4 rounded-sm border border-border" data-id="element-96">
                <label className="block text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2" data-id="element-97">
                  Requesting As
                </label>
                <div className="text-sm font-medium text-text-primary" data-id="element-98">
                  {brandName}
                </div>
              </div> : <div data-id="element-99">
                <label className="block text-sm font-medium text-text-primary mb-3" data-id="element-100">
                  1. Select Brand <span className="text-urgent" data-id="element-101">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-102">
                  {displayedBrands.map(brand => <label key={brand.id} className={`
                        flex items-center p-2 border rounded-sm cursor-pointer transition-colors
                        ${selectedBrand === brand.id ? 'border-primary bg-primary/5' : 'border-border hover:border-text-tertiary'}
                      `} data-id="element-103">
                      <input type="radio" name="brand" value={brand.id} checked={selectedBrand === brand.id} onChange={e => setSelectedBrand(e.target.value)} className="sr-only" data-id="element-104" />
                      <div className="flex-1 min-w-0" data-id="element-105">
                        <div className="text-sm font-medium text-text-primary truncate" data-id="element-106">
                          {brand.name}
                        </div>
                      </div>
                      <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ml-2 flex-shrink-0
                        ${selectedBrand === brand.id ? 'border-primary' : 'border-text-tertiary'}
                      `} data-id="element-107">
                        {selectedBrand === brand.id && <div className="w-1.5 h-1.5 bg-primary rounded-full" data-id="element-108" />}
                      </div>
                    </label>)}
                </div>

                {!showAllBrands && brands.length > 6 && <button type="button" onClick={() => setShowAllBrands(true)} className="mt-2 text-xs text-text-secondary hover:text-primary font-medium flex items-center gap-1 w-full justify-center py-1.5 border border-dashed border-border rounded-sm hover:bg-surface transition-colors" data-id="element-109">
                    <ChevronDownIcon className="w-3.5 h-3.5" data-id="element-110" />
                    Load More Brands
                  </button>}
              </div>}

            {/* 2. Contact Person (Only show if brand selected and NOT brand-initiated) */}
            {selectedBrand && !isBrandInitiated && <div className="bg-surface-warm p-4 rounded-sm border border-border" data-id="element-111">
                <div className="flex items-center justify-between mb-3" data-id="element-112">
                  <label className="block text-sm font-medium text-text-primary" data-id="element-113">
                    2. Contact Person <span className="text-urgent" data-id="element-114">*</span>
                  </label>
                  {!isAddingContact && <button onClick={() => setIsAddingContact(true)} className="text-xs font-medium text-primary hover:text-primary-dark flex items-center gap-1" data-id="element-115">
                      <PlusIcon className="w-3 h-3" data-id="element-116" />
                      Add New Contact
                    </button>}
                </div>

                {!isAddingContact && contacts.length > 0 ? <Select value={selectedContactId} onChange={e => setSelectedContactId(e.target.value)} options={contacts.map(c => ({
              value: c.id,
              label: `${c.name} (${c.email}) ${c.jobTitle ? `- ${c.jobTitle}` : ''}`
            }))} data-id="element-117" /> : <div className="space-y-3 bg-white p-3 border border-border rounded-sm" data-id="element-118">
                    <div className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1" data-id="element-119">
                      New Contact Details
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-120">
                      <div data-id="element-121">
                        <label className="block text-xs text-text-secondary mb-1" data-id="element-122">
                          Name *
                        </label>
                        <input type="text" value={newContact.name} onChange={e => setNewContact({
                    ...newContact,
                    name: e.target.value
                  })} className="w-full px-3 py-1.5 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" placeholder="e.g. Jane Doe" data-id="element-123" />
                      </div>
                      <div data-id="element-124">
                        <label className="block text-xs text-text-secondary mb-1" data-id="element-125">
                          Email *
                        </label>
                        <input type="email" value={newContact.email} onChange={e => setNewContact({
                    ...newContact,
                    email: e.target.value
                  })} className="w-full px-3 py-1.5 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" placeholder="jane@example.com" data-id="element-126" />
                      </div>
                    </div>
                    <div data-id="element-127">
                      <label className="block text-xs text-text-secondary mb-1" data-id="element-128">
                        Job Title
                      </label>
                      <input type="text" value={newContact.jobTitle} onChange={e => setNewContact({
                  ...newContact,
                  jobTitle: e.target.value
                })} className="w-full px-3 py-1.5 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" placeholder="e.g. Marketing Director" data-id="element-129" />
                    </div>
                    <div className="flex justify-end gap-2 mt-2" data-id="element-130">
                      {contacts.length > 0 && <Button variant="ghost" size="sm" onClick={() => setIsAddingContact(false)} data-id="element-131">
                          Cancel
                        </Button>}
                      <Button variant="secondary" size="sm" onClick={handleSaveNewContact} disabled={!newContact.name || !newContact.email} data-id="element-132">
                        Save Contact
                      </Button>
                    </div>
                  </div>}
              </div>}

            {/* Campaign Name */}
            <div data-id="element-133">
              <label className="block text-sm font-medium text-text-primary mb-2" data-id="element-134">
                {isBrandInitiated ? '1' : '3'}. Campaign Name
              </label>
              <input type="text" value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="e.g. Summer Beauty Launch..." className="w-full px-3 py-2 border border-border rounded-sm text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" data-id="element-135" />
            </div>

            {/* 4. Store Selection */}
            <div data-id="element-136">
              <div className="flex items-center justify-between mb-3" data-id="element-137">
                <label className="block text-sm font-medium text-text-primary" data-id="element-138">
                  {isBrandInitiated ? '2' : '4'}. Select Stores{' '}
                  <span className="text-urgent" data-id="element-139">*</span>
                </label>
                <button onClick={toggleAllStores} className="text-xs font-medium text-primary hover:text-primary-dark" data-id="element-140">
                  {selectedStoreIds.size === stores.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="border border-border rounded-sm max-h-48 overflow-y-auto bg-white" data-id="element-141">
                {stores.map(store => {
                const isSelected = selectedStoreIds.has(store.id);
                return <label key={store.id} className="flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-surface cursor-pointer" data-id="element-142">
                      {isSelected ? <CheckSquareIcon className="w-5 h-5 text-primary flex-shrink-0" data-id="element-143" /> : <SquareIcon className="w-5 h-5 text-text-tertiary flex-shrink-0" data-id="element-144" />}
                      <input type="checkbox" className="sr-only" checked={isSelected} onChange={() => toggleStore(store.id)} data-id="element-145" />
                      <div data-id="element-146">
                        <div className="text-sm font-medium text-text-primary" data-id="element-147">
                          {store.name}
                        </div>
                        <div className="text-xs text-text-secondary" data-id="element-148">
                          {store.province} · {store.tier.replace('-', ' ')}
                        </div>
                      </div>
                    </label>;
              })}
              </div>
              <div className="mt-2 text-xs text-text-tertiary text-right" data-id="element-149">
                {selectedStoreIds.size} of {stores.length} stores selected
              </div>
            </div>

            {/* 5. Select Week */}
            <div data-id="element-150">
              <label className="block text-sm font-medium text-text-primary mb-2" data-id="element-151">
                {isBrandInitiated ? '3' : '5'}. Select Booking Period
              </label>
              <div className="grid grid-cols-2 gap-4" data-id="element-152">
                <Select label="Start Week" value={startWeek.toString()} onChange={e => {
                const val = parseInt(e.target.value);
                setStartWeek(val);
                if (endWeek < val) setEndWeek(val);
              }} options={Array.from({
                length: 12
              }, (_, i) => {
                const baseDate = new Date('2026-03-03');
                const weekStart = new Date(baseDate);
                weekStart.setDate(baseDate.getDate() + i * 7);
                const month = weekStart.toLocaleDateString('en-ZA', {
                  month: 'short'
                });
                const day = weekStart.getDate();
                return {
                  value: (i + 1).toString(),
                  label: `Week ${i + 1} (${month} ${day})`
                };
              })} data-id="element-153" />
                <Select label="End Week" value={endWeek.toString()} onChange={e => setEndWeek(parseInt(e.target.value))} options={Array.from({
                length: 12
              }, (_, i) => {
                const baseDate = new Date('2026-03-03');
                const weekStart = new Date(baseDate);
                weekStart.setDate(baseDate.getDate() + i * 7);
                const month = weekStart.toLocaleDateString('en-ZA', {
                  month: 'short'
                });
                const day = weekStart.getDate();
                return {
                  value: (i + 1).toString(),
                  label: `Week ${i + 1} (${month} ${day})`
                };
              }).filter(opt => parseInt(opt.value) >= startWeek)} data-id="element-154" />
              </div>
              {startWeek > endWeek && <p className="text-xs text-urgent mt-1.5" data-id="element-155">
                  End week must be on or after start week.
                </p>}
            </div>

            {/* 6. Select Positions */}
            <div data-id="element-156">
              <div className="flex items-center justify-between mb-3" data-id="element-157">
                <label className="block text-sm font-medium text-text-primary" data-id="element-158">
                  {isBrandInitiated ? '4' : '6'}. Select Positions{' '}
                  <span className="text-urgent" data-id="element-159">*</span>
                </label>
                <div className="flex items-center gap-2" data-id="element-160">
                  <span className="text-xs text-text-tertiary" data-id="element-161">Type:</span>
                  <select value={positionTypeFilter} onChange={e => {
                  const newFilter = e.target.value as 'all' | 'outpost' | 'cylinder';
                  setPositionTypeFilter(newFilter);
                  // Only keep currently selected positions that match the new filter
                  if (selectedStoreIds.size > 0 && selectedPositionIds.size > 0) {
                    const validPositionIds = new Set<string>();
                    stores.filter(s => selectedStoreIds.has(s.id)).forEach(store => {
                      store.positions?.filter(p => (newFilter === 'all' || p.type === newFilter) && selectedPositionIds.has(p.id)).forEach(p => validPositionIds.add(p.id));
                    });
                    setSelectedPositionIds(validPositionIds);
                  }
                }} className="text-xs border border-border rounded-md px-2 py-1.5 bg-white text-text-primary focus:outline-none focus:ring-1 focus:ring-primary" data-id="element-162">
                    <option value="all" data-id="element-163">All Positions</option>
                    <option value="outpost" data-id="element-164">Outposts — R3,250/wk</option>
                    <option value="cylinder" data-id="element-165">Drive Aisles — R3,750/wk</option>
                  </select>
                </div>
              </div>

              {selectedStoreIds.size === 0 ? <p className="text-xs text-text-tertiary italic py-3" data-id="element-166">
                  Select at least one store to see position availability.
                </p> : <div className="space-y-3" data-id="element-167">
                  {stores.filter(s => selectedStoreIds.has(s.id)).map(store => {
                const isExpanded = expandedStoreId === store.id;
                const filteredPositions = store.positions?.filter(p => positionTypeFilter === 'all' || p.type === positionTypeFilter) || [];
                const selectedCount = filteredPositions.filter(p => selectedPositionIds.has(p.id)).length;
                return <div key={store.id} className="border border-border rounded-sm overflow-hidden bg-white" data-id="element-168">
                          <button type="button" onClick={() => setExpandedStoreId(isExpanded ? null : store.id)} className="w-full flex items-center justify-between p-3 bg-surface-warm hover:bg-surface transition-colors" data-id="element-169">
                            <div className="flex items-center gap-3" data-id="element-170">
                              <div className="text-sm font-medium text-text-primary" data-id="element-171">
                                {store.name}
                              </div>
                              <div className="text-xs text-text-secondary" data-id="element-172">
                                {store.tier.replace('-', ' ')}
                              </div>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-173">
                              {selectedCount > 0 && <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full" data-id="element-174">
                                  {selectedCount} selected
                                </span>}
                              <ChevronDownIcon className={`w-4 h-4 text-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-175" />
                            </div>
                          </button>

                          {isExpanded && filteredPositions.length > 0 && <div className="divide-y divide-border border-t border-border" data-id="element-176">
                              {/* Header row with Select All + week labels */}
                              <div className="flex items-center px-3 py-2 bg-surface" data-id="element-177">
                                <div className="flex-1 flex items-center gap-2" data-id="element-178">
                                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-179">
                                    Position
                                  </span>
                                  <button type="button" onClick={e => {
                          e.stopPropagation();
                          const allIds = filteredPositions.map(p => p.id);
                          const allSelected = allIds.every(id => selectedPositionIds.has(id));
                          setSelectedPositionIds(prev => {
                            const next = new Set(prev);
                            allIds.forEach(id => {
                              if (allSelected) next.delete(id);else next.add(id);
                            });
                            return next;
                          });
                        }} className="text-[10px] font-medium text-primary hover:text-primary/80 transition-colors" data-id="element-180">
                                    {filteredPositions.every(p => selectedPositionIds.has(p.id)) ? 'Deselect All' : 'Select All'}
                                  </button>
                                </div>
                                <div className="flex items-center gap-1" data-id="element-181">
                                  {Array.from({
                          length: endWeek - startWeek + 1
                        }, (_, i) => startWeek + i).map(w => <div key={w} className="w-6 text-center text-[10px] font-medium text-text-tertiary" title={`Week ${w}`} data-id="element-182">
                                      W{w}
                                    </div>)}
                                </div>
                              </div>

                              {filteredPositions.map(position => {
                      const isSelected = selectedPositionIds.has(position.id);
                      return <label key={position.id} className={`flex items-center px-3 py-2 cursor-pointer transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-surface'}`} data-id="element-183">
                                    <div className="flex-1 flex items-center gap-3" data-id="element-184">
                                      {isSelected ? <CheckSquareIcon className="w-4 h-4 text-primary flex-shrink-0" data-id="element-185" /> : <SquareIcon className="w-4 h-4 text-text-tertiary flex-shrink-0" data-id="element-186" />}
                                      <input type="checkbox" className="sr-only" checked={isSelected} onChange={() => {
                            setSelectedPositionIds(prev => {
                              const next = new Set(prev);
                              if (next.has(position.id)) next.delete(position.id);else next.add(position.id);
                              return next;
                            });
                          }} data-id="element-187" />
                                      <div className="flex items-center gap-2" data-id="element-188">
                                        <span className="text-sm text-text-primary" data-id="element-189">
                                          {position.name}
                                        </span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium ${position.type === 'outpost' ? 'bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200' : 'bg-green-50 text-green-700 border border-green-200'}`} data-id="element-190">
                                          {position.type === 'outpost' ? 'Outpost · R3,250' : 'Drive Aisle · R3,750'}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-1" data-id="element-191">
                                      {Array.from({
                            length: endWeek - startWeek + 1
                          }, (_, i) => startWeek + i).map(w => {
                            const slot = store.slots.find(s => s.positionId === position.id && s.weekNumber === w);
                            let dotColor = 'bg-border';
                            if (slot?.status === 'available') dotColor = 'bg-available';else if (slot?.status === 'booked' || slot?.status === 'internal') dotColor = 'bg-booked';else if (slot?.status === 'blocked') dotColor = 'bg-blocked';
                            return <div key={w} className="w-6 flex justify-center" title={`Week ${w}: ${slot?.status || 'unknown'}`} data-id="element-192">
                                            <div className={`w-2 h-2 rounded-full ${dotColor}`} data-id="element-193" />
                                          </div>;
                          })}
                                    </div>
                                  </label>;
                    })}
                            </div>}
                          {isExpanded && filteredPositions.length === 0 && <div className="px-3 py-4 text-xs text-text-tertiary italic text-center border-t border-border" data-id="element-194">
                              No{' '}
                              {positionTypeFilter === 'outpost' ? 'outpost' : 'drive aisle'}{' '}
                              positions at this store.
                            </div>}
                        </div>;
              })}
                </div>}
            </div>
          </div>

          {/* Cost Estimate Summary — shows when selections are made */}
          {costEstimate.total > 0 && <div className="px-6 py-4 border-t border-border bg-surface-warm" data-id="element-195">
              <button type="button" onClick={() => setShowEstimate(!showEstimate)} className="w-full flex items-center justify-between" data-id="element-196">
                <div className="flex items-center gap-2" data-id="element-197">
                  <ReceiptIcon className="w-4 h-4 text-text-secondary" data-id="element-198" />
                  <span className="text-sm font-semibold text-text-primary" data-id="element-199">
                    Estimated Investment
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-200">
                  <span className="text-lg font-bold text-text-primary" data-id="element-201">
                    {formatZAR(costEstimate.total)}
                  </span>
                  <ChevronDownIcon className={`w-4 h-4 text-text-tertiary transition-transform ${showEstimate ? 'rotate-180' : ''}`} data-id="element-202" />
                </div>
              </button>

              {showEstimate && <div className="mt-3 space-y-2" data-id="element-203">
                  {costEstimate.lineItems.map(item => <div key={item.mediaType} className="flex items-center justify-between text-sm py-1.5 border-b border-border/50 last:border-0" data-id="element-204">
                      <div data-id="element-205">
                        <span className="font-medium text-text-primary" data-id="element-206">
                          {item.mediaType}
                        </span>
                        <span className="text-text-tertiary ml-1" data-id="element-207">
                          — {item.positionCount} position
                          {item.positionCount !== 1 ? 's' : ''} across{' '}
                          {item.storeCount} store
                          {item.storeCount !== 1 ? 's' : ''} ×{' '}
                          {formatZAR(item.unitPrice)}
                          {startWeek !== endWeek ? ` × ${endWeek - startWeek + 1} weeks` : ''}
                        </span>
                      </div>
                      <span className="font-medium text-text-primary" data-id="element-208">
                        {formatZAR(item.subtotal)}
                      </span>
                    </div>)}
                  <div className="flex items-center justify-between pt-2 border-t border-border" data-id="element-209">
                    <span className="text-sm font-semibold text-text-primary" data-id="element-210">
                      Total{' '}
                      {startWeek !== endWeek ? `(for ${endWeek - startWeek + 1} weeks)` : '(per week)'}
                    </span>
                    <span className="text-sm font-bold text-text-primary" data-id="element-211">
                      {formatZAR(costEstimate.total)}
                    </span>
                  </div>
                  <p className="text-[11px] text-text-tertiary mt-1 leading-relaxed" data-id="element-212">
                    This is a cost estimate only. Your campaign is only
                    confirmed once Woolworths has approved your booking and you
                    have provided a purchase order to Grapevine.
                  </p>
                </div>}
            </div>}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-surface space-y-3" data-id="element-213">
            {!isBrandInitiated && <>
                <label className={`
                    flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors
                    ${sendToBrand ? 'border-primary bg-primary/5' : 'border-border bg-white hover:bg-surface'}
                  `} data-id="element-214">
                  <input type="checkbox" checked={sendToBrand} onChange={e => setSendToBrand(e.target.checked)} className="w-4 h-4 rounded-sm border-border text-primary focus:ring-primary" data-id="element-215" />
                  <div data-id="element-216">
                    <div className="text-sm font-medium text-text-primary" data-id="element-217">
                      Send request to brand now
                    </div>
                    <div className="text-xs text-text-secondary" data-id="element-218">
                      {sendToBrand ? 'The brand will be notified immediately' : 'Leave unticked to save as draft and send later.'}
                    </div>
                  </div>
                </label>
                {sendToBrand && !hasContact && <p className="text-xs text-urgent px-1" data-id="element-219">
                    A contact person is required to send the request to the
                    brand.
                  </p>}
              </>}
            <div className="flex items-center justify-between" data-id="element-220">
              <div className="flex items-center gap-2" data-id="element-221">
                <Button variant="ghost" onClick={onClose} data-id="element-222">
                  Cancel
                </Button>
                {costEstimate.total > 0 && <Button variant="ghost" size="sm" onClick={() => {
                const selectedStoresList = stores.filter(s => selectedStoreIds.has(s.id));
                const baseDate = new Date('2026-03-03');
                const weekStart = new Date(baseDate);
                weekStart.setDate(baseDate.getDate() + (startWeek - 1) * 7);
                const weekLabel = weekStart.toLocaleDateString('en-ZA', {
                  day: 'numeric',
                  month: 'short'
                });
                generateEstimatePDF({
                  brandName: brands.find(b => b.id === selectedBrand)?.name || brandName,
                  campaignName: campaignName || 'Untitled Campaign',
                  weekNumber: startWeek,
                  endWeekNumber: startWeek !== endWeek ? endWeek : undefined,
                  weekLabel,
                  mediaTypes: Array.from(new Set(costEstimate.lineItems.map(item => item.mediaType))),
                  storeCount: selectedStoreIds.size,
                  lineItems: costEstimate.lineItems,
                  total: costEstimate.total,
                  storeNames: selectedStoresList.map(s => s.name)
                });
              }} className="flex items-center gap-1.5 text-text-secondary" data-id="element-223">
                    <DownloadIcon className="w-3.5 h-3.5" data-id="element-224" />
                    Download Estimate
                  </Button>}
              </div>
              <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid || isSubmitting} className="flex items-center gap-2" data-id="element-225">
                {isSubmitting ? 'Processing...' : isBrandInitiated ? <>
                    <SendIcon className="w-4 h-4" data-id="element-226" />
                    Submit Request
                  </> : sendToBrand ? <>
                    <SendIcon className="w-4 h-4" data-id="element-227" />
                    Send Request
                  </> : 'Save as Draft'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>;
}